import { useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { MuiMarkdown } from 'mui-markdown'
import { sortBy } from 'ramda'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { MotherIcon } from 'src/v3/core/icons'
import {
  createSentimentAnalysis,
  createCourseSentimentAnalysis,
  fetchEventSentimentAnalyses,
  fetchCourseSentimentAnalyses,
  pollSentimentAnalysisStatus,
  pollCourseSentimentAnalysisStatus,
  selectActiveSentimentPolling,
  selectEventSentimentAnalysesByEventID,
  selectCourseSentimentAnalysesByCourseID,
  selectSentimentAnalysisStatusByEventID,
  selectCourseSentimentAnalysisStatusByCourseID,
  selectSentimentPollingProgress,
  EventSentimentAnalysis,
  CourseSentimentAnalysis,
} from 'src/v3/content-pipeline/performance.slice'
import { markdownStyles } from 'src/v3/utilities/markdown-styles'
import MotherThinking from 'src/v3/components/mother-thinking'
import PushPinIcon from '@mui/icons-material/PushPin'
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined'
import ReplayIcon from '@mui/icons-material/Replay'

type SentimentAnalysisSectionProps = {
  domain: 'live' | 'enduring'
  itemID: string
  licenseGroup: string
  startDate: string
  endDate: string
}

type AnalysisItem = EventSentimentAnalysis | CourseSentimentAnalysis

let PINS_STORAGE_KEY = 'sentiment-analysis-pinned'

let getPinnedIds = (): string[] => {
  try {
    let stored = localStorage.getItem(PINS_STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

let togglePin = (id: string): string[] => {
  let pins = getPinnedIds()
  let updated = pins.includes(id) ? pins.filter((p) => p !== id) : [...pins, id]
  localStorage.setItem(PINS_STORAGE_KEY, JSON.stringify(updated))
  return updated
}

let quickPrompts = [
  {
    label: 'Thorough analysis (comprehensive)',
    prompt: `Analyze all feedback data and provide a comprehensive sentiment analysis. Focus on being actionable, specific, and evidence-based.

**What we care about most:**
- Identify critical issues that need immediate attention (content errors, technical problems, serious concerns)
- Surface what's working well so we can amplify it
- Find patterns in what students struggle with or request
- Highlight professional impact stories and career outcomes
- Give specific, prioritized recommendations with clear next steps

**Your analysis should:**
- Use direct quotes as evidence when they're impactful
- Quantify when possible (e.g., "mentioned frequently by ~30% of respondents")
- Distinguish between urgent issues vs. nice-to-haves
- Surface non-obvious patterns or correlations
- Separate quick wins from longer-term improvements
- Note differences across license groups if they're significant

**Key areas to explore:**
- Overall sentiment and engagement
- Content quality: What's resonating? What's missing or confusing?
- Instructor performance and teaching effectiveness
- Technical issues: quiz errors, missing materials, media problems
- Course enhancement requests and content gaps
- Professional impact: How has this affected their practice?
- Unexpected insights or emerging themes

Be thorough and analytical. Don't worry about being concise—we want depth and insight. Organize your findings in whatever way tells the story most clearly.`,
  },
  {
    label: 'Brief summary highlighting negative feedback',
    prompt: 'Provide a brief summary of themes, highlighting negative feedback',
  },
  {
    label: 'Instructor performance report card',
    prompt:
      'Give me a report-card style analysis of the instructors performance',
  },
  {
    label: 'Common themes across all feedback',
    prompt:
      'Identify common themes of feedback for all short answer questions in the post course survey',
  },
  {
    label: 'Course requests and enhancement suggestions',
    prompt:
      'Extract any course/content requests, enhancement suggestions, or areas where learners want additional content',
  },
  {
    label: 'Areas of struggle with content',
    prompt:
      'Identify areas where learners reported struggling with the content or found topics challenging',
  },
  {
    label: 'Professional impact stories',
    prompt:
      'Highlight instances where learners reported how the education has impacted their professional career, practice, or work',
  },
  {
    label: 'Technical issues and errors',
    prompt:
      'Identify any mentions of: quiz mistakes or errors, missing show notes attachments, or errors in video/sound/media quality',
  },
]

let getThinkingMessage = (progress: any[]): string => {
  if (progress.length === 0) return 'Preparing sentiment analysis...'
  let lastProgress = progress[progress.length - 1]
  return lastProgress.message || 'Analyzing sentiment...'
}

let capitalizeLicenseGroup = (group: string): string => {
  let groupMap: Record<string, string> = {
    paramedic: 'Paramedic',
    emt: 'EMT',
    aemt: 'AEMT',
    nurse: 'Nurse',
    other: 'Other',
    all: 'All',
  }
  return groupMap[group.toLowerCase()] || group
}

let extractSummary = (markdown: string): string => {
  let lines = markdown
    .split('\n')
    .map((l) => l.trim())
    .filter(
      (l) =>
        l.length > 0 &&
        !l.startsWith('#') &&
        !l.startsWith('---') &&
        !l.startsWith('|') &&
        !l.startsWith('```') &&
        !l.startsWith('>') &&
        !l.startsWith('*') &&
        !l.startsWith('-'),
    )
  let first = lines[0] || ''
  if (first.length > 200) return `${first.slice(0, 197)}...`
  return first
}

let formatAnalysisLabel = (analysis: AnalysisItem): string => {
  if (analysis.name) return analysis.name
  let start = analysis.startDate
    ? new Date(analysis.startDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    : ''
  let end = analysis.endDate
    ? new Date(analysis.endDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : ''
  return start && end ? `${start} - ${end}` : 'Unnamed analysis'
}

let AnalysisList = ({
  analyses,
  selectedId,
  pinnedIds,
  onSelect,
  onTogglePin,
}: {
  analyses: AnalysisItem[]
  selectedId: string
  pinnedIds: string[]
  onSelect: (id: string) => void
  onTogglePin: (id: string) => void
}) => {
  let sorted = sortBy((a: AnalysisItem) => {
    let pinned = pinnedIds.includes(a.id) ? 0 : 1
    let time = a.createdTime ? -new Date(a.createdTime).getTime() : 0
    return pinned * 1e15 + time
  }, analyses)

  return (
    <Box
      sx={{
        maxHeight: 320,
        overflowY: 'auto',
        borderRadius: 1.5,
        bgcolor: 'background.paperDark',
        border: '1px solid',
        borderColor: 'divider',
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'divider',
          borderRadius: 2,
        },
      }}
    >
      {sorted.map((analysis, index) => {
        let isSelected = analysis.id === selectedId
        let isPinned = pinnedIds.includes(analysis.id)
        let statusColor =
          analysis.status === 'SessionCompleted'
            ? 'success.main'
            : analysis.status === 'SessionFailed'
              ? 'error.main'
              : 'warning.main'
        let statusLabel =
          analysis.status === 'SessionCompleted'
            ? 'Completed'
            : analysis.status === 'SessionFailed'
              ? 'Failed'
              : 'In Progress'
        let createdLabel = analysis.createdTime
          ? new Date(analysis.createdTime).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            })
          : ''
        let summary = analysis.result ? extractSummary(analysis.result) : ''

        return (
          <Box
            key={analysis.id}
            onClick={() => onSelect(analysis.id)}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              px: 2,
              py: 1.5,
              cursor: 'pointer',
              bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
              borderLeft: '3px solid',
              borderLeftColor: isSelected ? 'primary.main' : 'transparent',
              borderBottom: index < sorted.length - 1 ? '1px solid' : 'none',
              borderBottomColor: 'divider',
              transition: 'background-color 0.15s',
              '&:hover': {
                bgcolor: isSelected
                  ? 'rgba(25, 118, 210, 0.12)'
                  : 'rgba(255, 255, 255, 0.03)',
              },
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: statusColor,
                flexShrink: 0,
                mt: 0.75,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 0.25,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isSelected ? 600 : 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatAnalysisLabel(analysis)}
                </Typography>
                <Chip
                  label={capitalizeLicenseGroup(analysis.licenseGroup)}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.65rem',
                    height: 18,
                    flexShrink: 0,
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              </Box>

              {summary && (
                <Typography
                  color="text.secondary"
                  variant="caption"
                  sx={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.4,
                  }}
                >
                  {summary}
                </Typography>
              )}

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 0.25,
                }}
              >
                {createdLabel && (
                  <Typography
                    color="text.disabled"
                    variant="caption"
                    sx={{
                      fontSize: '0.7rem',
                    }}
                  >
                    {createdLabel}
                  </Typography>
                )}
                <Typography
                  color="text.disabled"
                  variant="caption"
                  sx={{
                    fontSize: '0.7rem',
                  }}
                >
                  · {statusLabel}
                </Typography>
              </Box>
            </Box>
            <Tooltip title={isPinned ? 'Unpin' : 'Pin'}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  onTogglePin(analysis.id)
                }}
                sx={{
                  mt: 0.25,
                  color: isPinned ? 'primary.main' : 'text.disabled',
                  opacity: isPinned ? 1 : 0.5,
                  '&:hover': { opacity: 1 },
                }}
              >
                {isPinned ? (
                  <PushPinIcon sx={{ fontSize: 16 }} />
                ) : (
                  <PushPinOutlinedIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        )
      })}
    </Box>
  )
}

let AnalysisMetadataBar = ({
  analysis,
  isPinned,
  onTogglePin,
}: {
  analysis: AnalysisItem
  isPinned: boolean
  onTogglePin: () => void
}) => {
  let startLabel = analysis.startDate
    ? new Date(analysis.startDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Unknown'
  let endLabel = analysis.endDate
    ? new Date(analysis.endDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Unknown'
  let createdLabel = analysis.createdTime
    ? new Date(analysis.createdTime).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Unknown'

  let statusLabel =
    analysis.status === 'SessionCompleted'
      ? 'Completed'
      : analysis.status === 'SessionFailed'
        ? 'Failed'
        : 'In Progress'
  let statusColor =
    analysis.status === 'SessionCompleted'
      ? 'success'
      : analysis.status === 'SessionFailed'
        ? 'error'
        : ('warning' as 'success' | 'error' | 'warning')

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        mb: 2,
      }}
    >
      <Typography color="text.secondary" variant="body2">
        Range:{' '}
        <Typography
          component="span"
          variant="body2"
          sx={{
            fontWeight: 600,
          }}
        >
          {startLabel} - {endLabel}
        </Typography>
      </Typography>
      <Typography color="text.secondary" variant="body2">
        License:{' '}
        <Typography
          component="span"
          variant="body2"
          sx={{
            fontWeight: 600,
          }}
        >
          {capitalizeLicenseGroup(analysis.licenseGroup)}
        </Typography>
      </Typography>
      <Typography color="text.secondary" variant="body2">
        Created:{' '}
        <Typography
          component="span"
          variant="body2"
          sx={{
            fontWeight: 600,
          }}
        >
          {createdLabel}
        </Typography>
      </Typography>
      <Typography color="text.secondary" variant="body2">
        Status:{' '}
        <Chip
          label={statusLabel}
          size="small"
          variant="outlined"
          sx={{
            color: statusColor,
            height: 22,
            fontSize: '0.75rem',
          }}
        />
      </Typography>
      <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={
            isPinned ? (
              <PushPinIcon sx={{ fontSize: 16 }} />
            ) : (
              <PushPinOutlinedIcon sx={{ fontSize: 16 }} />
            )
          }
          onClick={onTogglePin}
        >
          {isPinned ? 'Pinned' : 'Pin'}
        </Button>

        <Button
          size="small"
          variant="outlined"
          startIcon={<ReplayIcon sx={{ fontSize: 16 }} />}
          disabled
        >
          Re-run
        </Button>
      </Box>
    </Box>
  )
}

export let SentimentAnalysisSection = ({
  domain,
  itemID,
  licenseGroup,
  startDate,
  endDate,
}: SentimentAnalysisSectionProps) => {
  let dispatch = useAppDispatch()
  let eventAnalyses = useAppSelector(
    selectEventSentimentAnalysesByEventID(itemID),
  )
  let courseAnalyses = useAppSelector(
    selectCourseSentimentAnalysesByCourseID(itemID),
  )
  let eventStatus = useAppSelector(
    selectSentimentAnalysisStatusByEventID(itemID),
  )
  let courseStatus = useAppSelector(
    selectCourseSentimentAnalysisStatusByCourseID(itemID),
  )
  let activePolling = useAppSelector(selectActiveSentimentPolling)
  let pollingProgress = useAppSelector(selectSentimentPollingProgress)

  let analyses = domain === 'live' ? eventAnalyses : courseAnalyses
  let status = domain === 'live' ? eventStatus : courseStatus

  let [showPromptForm, setShowPromptForm] = useState(false)
  let [promptText, setPromptText] = useState('')
  let [analysisName, setAnalysisName] = useState('')
  let [selectedAnalysisID, setSelectedAnalysisID] = useState<string>('')
  let [fetchingResultFor, setFetchingResultFor] = useState<string | null>(null)
  let [isExpanded, setIsExpanded] = useState(false)
  let [pinnedIds, setPinnedIds] = useState<string[]>(getPinnedIds)
  let pollingInterval = useRef<number | undefined>(undefined)
  let textareaRef = useRef<HTMLTextAreaElement>(null)
  let sectionRef = useRef<HTMLDivElement>(null)

  let selectedAnalysis = analyses?.find((a) => a.id === selectedAnalysisID)
  let isPolling = !!(
    activePolling &&
    ((domain === 'live' && activePolling.eventID === itemID) ||
      (domain === 'enduring' && activePolling.courseID === itemID))
  )
  let isLoading = status === 'pending'

  useEffect(() => {
    setSelectedAnalysisID('')
    setShowPromptForm(false)
    setPromptText('')
    setAnalysisName('')
    if (domain === 'live') {
      dispatch(fetchEventSentimentAnalyses(itemID))
    } else {
      dispatch(fetchCourseSentimentAnalyses(itemID))
    }
  }, [dispatch, itemID, domain])

  useEffect(() => {
    if (analyses && analyses.length > 0 && !selectedAnalysisID) {
      let completedAnalyses = analyses.filter(
        (a) => a.status === 'SessionCompleted',
      )
      if (completedAnalyses.length > 0) {
        let sortedAnalyses = sortBy(
          (a) => -(a.createdTime ? new Date(a.createdTime).getTime() : 0),
          completedAnalyses,
        )
        setSelectedAnalysisID(sortedAnalyses[0].id)
      } else if (analyses.length > 0) {
        setSelectedAnalysisID(analyses[0].id)
      }
    }
  }, [analyses, selectedAnalysisID])

  useEffect(() => {
    if (
      selectedAnalysis &&
      !selectedAnalysis.result &&
      selectedAnalysis.status === 'SessionCompleted'
    ) {
      setFetchingResultFor(selectedAnalysis.correlationID)
      let pollFn =
        domain === 'live'
          ? pollSentimentAnalysisStatus
          : pollCourseSentimentAnalysisStatus
      dispatch(pollFn(selectedAnalysis.correlationID)).finally(() => {
        setFetchingResultFor(null)
      })
    }
  }, [selectedAnalysis, dispatch, domain])

  useEffect(() => {
    setIsExpanded(false)
  }, [selectedAnalysisID])

  useEffect(() => {
    if (textareaRef.current) {
      // eslint-disable-next-line fp/no-mutation
      textareaRef.current.style.height = 'auto'
      // eslint-disable-next-line fp/no-mutation
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [promptText])

  useEffect(() => {
    let isActiveForThisItem =
      (domain === 'live' && activePolling?.eventID === itemID) ||
      (domain === 'enduring' && activePolling?.courseID === itemID)

    if (activePolling && isActiveForThisItem) {
      let pollFn =
        domain === 'live'
          ? pollSentimentAnalysisStatus
          : pollCourseSentimentAnalysisStatus

      let pollStatus = async () => {
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        let result = await dispatch(pollFn(activePolling.correlationID))
        if (result.payload) {
          let payload = result.payload as any
          if (
            payload.status === 'SessionCompleted' ||
            payload.status === 'SessionFailed'
          ) {
            clearInterval(pollingInterval.current)
            if (payload.status === 'SessionCompleted') {
              if (domain === 'live') {
                // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
                await dispatch(fetchEventSentimentAnalyses(itemID))
              } else {
                // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
                await dispatch(fetchCourseSentimentAnalyses(itemID))
              }
              setShowPromptForm(false)
              setPromptText('')
              setAnalysisName('')
            }
          }
        }
      }

      pollStatus()
      // eslint-disable-next-line fp/no-mutation
      pollingInterval.current = window.setInterval(pollStatus, 3000)
    }

    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current)
      }
    }
  }, [activePolling, dispatch, itemID, domain])

  let handleRunAnalysis = () => {
    setShowPromptForm(true)
  }

  let handleSubmitAnalysis = async () => {
    if (!promptText.trim()) return
    if (domain === 'live') {
      // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
      await dispatch(
        createSentimentAnalysis({
          eventID: itemID,
          licenseGroup,
          startDate,
          endDate,
          prompt: promptText,
          name: analysisName.trim() || undefined,
        }),
      )
    } else {
      // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
      await dispatch(
        createCourseSentimentAnalysis({
          courseID: itemID,
          licenseGroup,
          startDate,
          endDate,
          prompt: promptText,
          name: analysisName.trim() || undefined,
        }),
      )
    }
  }

  let handleCancelPrompt = () => {
    setShowPromptForm(false)
    setPromptText('')
    setAnalysisName('')
  }

  let handleQuickPromptClick = (prompt: string) => {
    setPromptText(prompt)
    setTimeout(() => {
      if (sectionRef.current) {
        sectionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }
    }, 0)
  }

  let handleTogglePin = (id: string) => {
    let updated = togglePin(id)
    setPinnedIds(updated)
  }

  let promptFormJSX = (
    <Box>
      <TextField
        fullWidth
        label="Analysis Name (optional)"
        value={analysisName}
        onChange={(e) => setAnalysisName(e.target.value)}
        placeholder="Give this analysis a name..."
        sx={{ mb: 2, bgcolor: 'background.paperDark' }}
      />
      <Typography variant="body2" sx={{ mb: 2 }}>
        Describe what you want to analyze:
      </Typography>
      <textarea
        ref={textareaRef}
        value={promptText}
        onChange={(e) => setPromptText(e.target.value)}
        placeholder="Describe what you want to analyze..."
        className="
          w-full
          min-h-[100px]
          p-4
          mb-4
          rounded
          resize-none
          overflow-hidden
          text-white
          placeholder-gray-500
          focus:outline-none
          focus:ring-2
          focus:ring-blue-500
        "
        style={{ backgroundColor: '#121212' }}
      />
      <Typography
        color="text.secondary"
        variant="body2"
        sx={{
          mb: 1,
        }}
      >
        Quick Prompts:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {quickPrompts.map((quickPrompt) => (
          <Chip
            key={quickPrompt.label}
            label={quickPrompt.label}
            onClick={() => handleQuickPromptClick(quickPrompt.prompt)}
            clickable
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="contained"
          onClick={handleSubmitAnalysis}
          disabled={!promptText.trim()}
        >
          Run Analysis
        </Button>
        <Button variant="outlined" onClick={handleCancelPrompt}>
          Cancel
        </Button>
      </Box>
    </Box>
  )

  let headerJSX = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 2,
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 700,
            }}
          >
            Sentiment Analysis
          </Typography>
          <Chip
            icon={<MotherIcon />}
            label="MOTHER · Deep Dive"
            size="small"
            variant="outlined"
          />
        </Box>
        <Typography color="text.secondary" variant="body2">
          Long-form analyses of open-ended feedback. Pin your favorites for
          quick access.
        </Typography>
      </Box>
      <Button
        variant="contained"
        startIcon={<MotherIcon />}
        onClick={handleRunAnalysis}
        disabled={isPolling}
      >
        + Run New Analysis
      </Button>
    </Box>
  )

  if (!analyses || analyses.length === 0) {
    if (isLoading) {
      return (
        <Card variant="outlined" sx={{ p: 3 }} ref={sectionRef}>
          {headerJSX}
          <Skeleton
            variant="rectangular"
            sx={{
              height: 60,
            }}
          />
          <Skeleton variant="text" sx={{ mt: 2 }} />
          <Skeleton variant="text" />
        </Card>
      )
    }

    return (
      <Card variant="outlined" sx={{ p: 3 }} ref={sectionRef}>
        {headerJSX}
        {showPromptForm && !isPolling && promptFormJSX}
        {!showPromptForm && !isPolling && (
          <Typography color="text.secondary" variant="body2">
            No analyses yet. Run a sentiment analysis to get MOTHER-powered
            insights from student feedback.
          </Typography>
        )}
        {isPolling && (
          <MotherThinking
            isThinking={true}
            currentMessage={getThinkingMessage(pollingProgress)}
            size="medium"
          />
        )}
      </Card>
    )
  }

  return (
    <Card variant="outlined" sx={{ p: 3 }} ref={sectionRef}>
      {headerJSX}
      <AnalysisList
        analyses={analyses}
        selectedId={selectedAnalysisID}
        pinnedIds={pinnedIds}
        onSelect={setSelectedAnalysisID}
        onTogglePin={handleTogglePin}
      />
      {showPromptForm && !isPolling && (
        <Box sx={{ mt: 2, mb: 3 }}>{promptFormJSX}</Box>
      )}
      {isPolling && (
        <Box sx={{ mt: 2, mb: 3 }}>
          <MotherThinking
            isThinking={true}
            currentMessage={getThinkingMessage(pollingProgress)}
            size="medium"
          />
        </Box>
      )}
      {!showPromptForm && !isPolling && selectedAnalysis && (
        <Box sx={{ mt: 2 }}>
          <AnalysisMetadataBar
            analysis={selectedAnalysis}
            isPinned={pinnedIds.includes(selectedAnalysis.id)}
            onTogglePin={() => handleTogglePin(selectedAnalysis.id)}
          />

          <Box sx={markdownStyles}>
            {selectedAnalysis.result ? (
              <Box sx={{ position: 'relative' }}>
                <div
                  className={
                    isExpanded ? 'max-h-none' : 'max-h-[400px] overflow-hidden'
                  }
                >
                  <MuiMarkdown>{selectedAnalysis.result}</MuiMarkdown>
                </div>

                {!isExpanded && (
                  <div
                    className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
                    style={{
                      background:
                        'linear-gradient(to bottom, transparent, #1e2124)',
                    }}
                  />
                )}

                <Button
                  onClick={() => setIsExpanded(!isExpanded)}
                  sx={{ mt: 2, width: '100%' }}
                  variant="text"
                >
                  {isExpanded ? 'Show Less' : 'Show More'}
                </Button>
              </Box>
            ) : selectedAnalysis.status === 'SessionFailed' ? (
              <Box>
                <Typography color="error.main" variant="body2">
                  This sentiment analysis failed to complete.
                </Typography>
                <Typography
                  color="text.secondary"
                  variant="caption"
                  sx={{
                    mt: 1,
                    display: 'block',
                  }}
                >
                  Analysis ID: {selectedAnalysis.id}
                  <br />
                  Correlation ID: {selectedAnalysis.correlationID}
                  <br />
                  Created:{' '}
                  {selectedAnalysis.createdTime
                    ? new Date(selectedAnalysis.createdTime).toLocaleString()
                    : 'Unknown'}
                </Typography>
              </Box>
            ) : selectedAnalysis.status === 'SessionInProgress' ||
              fetchingResultFor === selectedAnalysis.correlationID ? (
              <Box>
                <Skeleton
                  variant="text"
                  sx={{
                    width: '100%',
                    height: 30,
                  }}
                />
                <Skeleton
                  variant="text"
                  sx={{
                    width: '100%',
                    height: 30,
                  }}
                />
                <Skeleton
                  variant="text"
                  sx={{
                    width: '80%',
                    height: 30,
                  }}
                />
              </Box>
            ) : null}
          </Box>
        </Box>
      )}
    </Card>
  )
}
